<template>
  <!-- A plain wrapper, deliberately WITHOUT `page-break-avoid`. It used to carry one, and that
       made the whole section indivisible whenever it happened to fit on a page: a section one
       percent too tall for the room left moved in its entirety, leaving most of a page blank,
       while a section too tall for ANY page quietly lost the avoid and flowed regardless — the
       same attribute producing opposite behaviour depending on how much data a module had. Only
       the head below must never be sliced; everything after it is free to break. -->
  <div>
    <!-- THE unit that must stay whole, matching ExecutiveSummarySecurityAwarenessSection: the
         banner, the heading, the description and the headline number + sparkline travel together.
         At ~250px it sits comfortably inside the 718px page, so html2pdf can always honour it —
         which matters, because it silently drops an `avoid` from anything taller than one printed
         page, and a section with ten ticket triggers is taller. Keep this block small for that
         reason: everything below it (the lists, and the `extra` slot) may start on this page and
         continue on the next, which is what lets a module head follow a full page of some other
         module's list instead of opening a page of its own. -->
    <div class="page-break-avoid">
      <!-- Anything a caller needs kept with this heading — the section banner that introduces it,
           for one — belongs inside the wrapper, not before it. -->
      <slot name="before"></slot>
      <!-- Icon, title and description all take Primary/Indigo Medium when the module is not
           subscribed — the mock names one token for the whole heading block, and the theme's
           `indigo-medium` is that exact value. Not an opacity helper: the mock specifies a
           colour, and `text--semitransparent` composites to something else over the card. -->
      <div class="d-flex align-center ga-2 mb-3">
        <v-icon size="40" :icon="moduleIcon" :class="{ 'text-indigo-medium': disabled }" />
        <span
          class="headline6"
          :class="{ 'text-indigo-medium': disabled }"
          data-testid="executive-summary-module-title"
        >
          {{ $t(`reports.executiveSummaryReport.modules.${viewModel.module}.title`) }}
        </span>
      </div>
      <div
        class="body1 mb-6"
        :class="{ 'text-indigo-medium': disabled }"
        data-testid="executive-summary-module-description"
      >
        {{ $t(`reports.executiveSummaryReport.modules.${viewModel.module}.description`) }}
      </div>

      <executive-summary-module-disabled-state v-if="disabled" :module="viewModel.module" />

      <div v-else class="d-flex ga-10 mb-10">
        <div class="d-flex flex-column executive-summary-module-section__stats">
          <div class="big-number text-primary" data-testid="executive-summary-module-total">
            {{ formatReportNumber(viewModel.total) }}
          </div>
          <div class="subtitle2">
            {{ $t("reports.executiveSummaryReport.ticketsGenerated") }}
          </div>
          <div class="body3 text-green-dark" data-testid="executive-summary-module-auto-resolved">
            {{ autoResolvedCaption }}
          </div>
        </div>
        <div class="flex-grow-1">
          <report-trend-chart :points="viewModel.points" :delta="viewModel.delta" />
        </div>
      </div>
    </div>

    <!-- Outside the unit above: this is the seam the section is allowed to split on. Gated on
         `disabled` in its own right — the placeholder replaces the body, so an unsubscribed module
         must not render these lists. -->
    <div v-if="!disabled" class="executive-summary-module-section__lists">
      <div>
        <executive-summary-bar-list
          :title="ticketsByTypeTitleResolved"
          :sub-title="ticketsByTypeSubTitle"
          :items="ticketsByType"
          :variant="ReportTilesVariant.TEAL"
          :show-zero-values="showZeroValues"
        />
      </div>
      <div>
        <executive-summary-bar-list
          :title="
            $t(`reports.executiveSummaryReport.modules.${viewModel.module}.topViolatorsTitle`)
          "
          :items="viewModel.violators"
          :variant="ReportTilesVariant.VIOLET"
          :show-zero-values="showZeroValues"
        />
      </div>
    </div>
  </div>

  <!-- Deliberately outside the wrapper: Cloud's bot-attacks card and Email's extras grid each
       push their section past one page, so they are their own page-break units and this seam is
       where those two sections are allowed to split. Still gated on `disabled` — an unsubscribed
       module shows the placeholder and nothing else, and moving this out of the `v-else` above
       is what would otherwise let its data through. -->
  <slot v-if="!disabled" name="extra"></slot>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import ReportTrendChart from "@/components/reports/ReportTrendChart.vue";
import ExecutiveSummaryBarList from "@/components/reports/executive-summary/ExecutiveSummaryBarList.vue";
import ExecutiveSummaryModuleDisabledState from "@/components/reports/executive-summary/ExecutiveSummaryModuleDisabledState.vue";
import { ReportTilesVariant, REPORT_MODULE_ICONS_S } from "@/constants/reports";
import { isModuleDisabled } from "@/_helpers/subscription";
import { formatReportNumber, formatReportPercentValue } from "@/_helpers/reports";
import type { ExecutiveSummaryModuleViewModel } from "@/_store/reports/adapters";
import type { TicketTrigger } from "@/constants/tickets";

export default defineComponent({
  components: {
    ReportTrendChart,
    ExecutiveSummaryBarList,
    ExecutiveSummaryModuleDisabledState,
  },
  props: {
    viewModel: {
      type: Object as PropType<ExecutiveSummaryModuleViewModel>,
      required: true,
    },
    showZeroValues: {
      type: Boolean,
      default: true,
    },
    // Overrides for the tickets-by-type list's heading. Email Security is the only
    // module with its own wording ("Remaining tickets by type (n tickets)") — every
    // other module leaves these unset and keeps today's plain "Tickets by type".
    ticketsByTypeTitle: {
      type: String,
      default: undefined,
    },
    ticketsByTypeSubTitle: {
      type: String,
      default: undefined,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const disabled = computed(() => isModuleDisabled(props.viewModel.module));

    const ticketsByTypeTitleResolved = computed(
      () => props.ticketsByTypeTitle ?? i18n.t("reports.executiveSummaryReport.ticketsByType")
    );

    const moduleIcon = computed(() => REPORT_MODULE_ICONS_S[props.viewModel.module]);

    const autoResolvedCaption = computed(() =>
      i18n.t("reports.executiveSummaryReport.autoResolved", {
        n: formatReportPercentValue(props.viewModel.autoResolvedPercent),
      })
    );

    // Sorted here rather than in the adapter: alphabetical order depends on the
    // translated trigger label, which the store layer cannot resolve.
    const ticketsByType = computed(() =>
      Object.entries(props.viewModel.vulnerabilities)
        .map(([trigger, value]) => ({
          // The enum, not the label: fr-FR translates domainImpersonation and domainSpoofing to
          // one string, and es-ES collides two criticalData triggers, so labels are not unique.
          id: trigger,
          label: i18n.t(`tickets.eventTriggers.${trigger as TicketTrigger}`),
          value,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );

    return {
      disabled,
      moduleIcon,
      autoResolvedCaption,
      ticketsByType,
      ticketsByTypeTitleResolved,
      ReportTilesVariant,
      formatReportNumber,
    };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-module-section__stats {
  min-width: 180px;
}

.v-icon.text-indigo-medium::before {
  color: inherit;
}

// A plain grid rather than v-row/v-col: Vuetify's row applies `margin: -12px` to cancel the
// columns' padding, which spills 12px past the report card on both sides — and the card clips.
// It also double-counted the gutter, since `ga-10`'s 40px gap sat on top of that padding.
.executive-summary-module-section__lists {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px;
}
</style>
